import { getJSONByUrl, submitFormJson } from 'widgets/toolbox/ajax';


/**
 * @typedef {typeof import('widgets/cart/MinicartDialog').default} MinicartDialog
 */

/**
 * @description Base HarmonyMiniCartDialog implementation
 * @param {ReturnType<typeof import('widgets/cart/MinicartDialog').default>} MinicartDialog Base widget for extending
 * @returns {typeof HarmonyMiniCartDialog} HarmonyMiniCartDialog class
 */
export default function (MinicartDialog) {
    return class HarmonyMiniCartDialog extends MinicartDialog {
        init() {
            super.init();
            this.eventBus().on('auto.close.minicart', 'closeMinicart');
            this.eventBus().on('cart.items.refresh', 'hiddenRefreshMinicart');
        }

        /**
         * @description Hidden refresh the minicart page
         * @returns {void}
         */
        hiddenRefreshMinicart() {
            getJSONByUrl(this.prefs().actionUrl)
                .then((response) => {
                    if (!response) {
                        return;
                    }
                    this.render(
                        undefined,
                        {},
                        this.ref(this.prefs().refDialog),
                        response.minicartHtml
                    );
                })
                .catch(() => {
                    this.eventBus().emit('alert.show', { errorCode: 500 });
                });
        }

        closeMinicart(autoCloseTimeout) {
            this.autoCloseTimeout = window.setTimeout(
                this.closeModal.bind(this),
                autoCloseTimeout
            );
        }

        cancel() {
            window.clearTimeout(this.autoCloseTimeout);
            super.cancel();
        }

        confirmedRemoveProduct(button, movedToWishlist = false) {
            if (this.removeProductLink) {
                this.eventBus().emit('minicart.remove.product', this);

                const uuid = this.removeProductLink.data('uuid');
                const pid = this.removeProductLink.data('pid');
                const promise = submitFormJson(
                    this.removeProductLink.data('removeAction') || this.removeProductLink.data('action'),
                    {
                        pid: pid,
                        uuid: uuid
                    },
                    'POST'
                ).then((response) => {
                    if (!response) {
                        return;
                    }

                    this.needUpdate = true;
                    this.eventBus().emit('minicart.updated', response.basket);
                    this.eventBus().emit('minicart.removed.product', pid);
                    this.accessibilityAlert(
                        movedToWishlist
                            ? this.prefs().accessibilityAlerts.movedtowishlist
                            : this.prefs().accessibilityAlerts.productremoved
                    );
                    if (response.basket.numItems) {
                        // this function renders updated minicart,
                        // for example when we change product quantity.
                        // But when we don't have products in minicart, this function renders modal window with text: 'Empty shopping cart',
                        // but accordint to FSD we don't need this window, we need just close mini cart, when last product was deleted
                        this.showMinicart();
                    } else {
                        this.cancel();
                    }
                });

                this.eventBus().emit('loader.start', promise);

                this.removeProductLink = null;
            }
        }

        /**
         * @description Updates quantity for concrete product in minicart widget
         * @param {inputSelect} inputSelect - Target select input in minicart widget, which triggers quantity update
         * @emits "minicart.updated"
         * @returns {void}
         */
        updateQty(inputSelect) {
            this.toggleSpinner(true);

            const uuid = inputSelect.data('uuid');
            const promise = submitFormJson(inputSelect.data('action'), {
                pid: inputSelect.data('pid'),
                uuid: uuid,
                quantity: inputSelect.getValue()
            }, 'GET')
                .catch(response => {
                    this.displayItemLevelErrorMessage(uuid, (response && response.message));
                })
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    this.needUpdate = true;
                    /**
                     * @description Event to notify concerned widgets, that Minicart was updated
                     * @event "minicart.updated"
                     */
                    this.eventBus().emit('minicart.updated', response);

                    if (!inputSelect.hasError) {
                        this.accessibilityAlert(`${this.prefs().accessibilityAlerts.quantitychanged} ${inputSelect.getValue()}`);
                    }

                    this.updateContent();
                })
                .finally(() => {
                    this.toggleSpinner(false);
                });

            this.eventBus().emit('loader.start', promise);
        }

        /**
         * @description Update minicart panel content
         * Fetch actual content in case when it needed. Use flag `needUpdate` for this
         * @returns {void}
         */
        updateContent() {
            if (!this.needUpdate) {
                return;
            }

            $('.b-loader').removeClass('h-hidden');

            getJSONByUrl(this.prefs().actionUrl)
                .then((response) => {
                    if (response
                        && response.redirectUrl
                        && this.handleRedirectUrl
                    ) {
                        window.location.assign(response.redirectUrl);
                        return;
                    }

                    this.handleRedirectUrl = false;
                    this.needUpdate = false;

                    this.render(
                        undefined,
                        {},
                        this.ref(this.prefs().refDialog),
                        response.minicartHtml
                    ).then(() => {
                        // Open modal after new template rendering
                        this.showModal({
                            attributes: {
                                'data-tau-unique': 'minicart_dialog'
                            }
                        });
                        this.ref(this.prefs().refDialog).addClass(this.prefs().classesActivated);
                        $('.b-loader').addClass('h-hidden');
                        this.addFocusTraps(); // enclose new content with focus trap
                    });
                })
                .catch(() => {
                    this.eventBus().emit('alert.show', { errorCode: 500 });
                    $('.b-loader').addClass('h-hidden');
                    this.close();
                });
        }

        /**
         * @description A method, called when event `click` happens on minicart icon
         * - Check cart total if empty redirect to cart page
         * - Shows minicart panel
         * - Update content of the panel
         * @returns {void}
         */
        showMinicart() {
            // If we make a request to update cart, then we need to open modal after receiving information
            if (this.needUpdate) {
                // We have to check response for the presence of redirectUrl,
                // to catch expired basket and redirect user to the empty cart page
                this.handleRedirectUrl = true;
                this.updateContent();

                return;
            }

            // Otherwise we open modal with the template we rendered earlier
            this.showModal({
                attributes: {
                    'data-tau-unique': 'minicart_dialog'
                }
            });
            this.ref(this.prefs().refDialog).addClass(this.prefs().classesActivated);
        }

        /**
         * @description Move product from minicart to wishlist.
         * In fact this functionality implemented in 2 steps:
         * 1. Add product to wishlist
         * 2. Remove product from minicart
         * This is done due to possible backend code duplication in one universal endpoint
         * @returns {void}
         */
        moveProductToWishlist() {
            if (this.removeProductLink) {
                const link = this.removeProductLink;
                const productID = link?.data('master-pid') || link?.data('pid');
                const productName = link?.data('name');
                submitFormJson(this.prefs().actionMovetowishlist, {
                    pid: productID
                }).then(() => {
                    this.confirmedRemoveProduct(null, true);
                    this.eventBus().emit('product.added.to.wishlist', productName);
                });
            }
        }
        openShipTo() {
            const shipToTarget = document.querySelector('.b-ship_to');
            if (shipToTarget) {
                shipToTarget.dispatchEvent(new Event('click'));
            }
        }
    };
}
